import { Check, AlertTriangle, Shield, Crown, Sparkles } from 'lucide-react';
import { money } from '../LiveBasket';
import AdminPanelShowcase from '../../AdminPanelShowcase';

function CarePlanCard({ plan, selected, onSelect }) {
  return (
    <button
      type="button"
      data-testid={`careplan-${plan.id}`}
      onClick={() => onSelect(plan)}
      className={`group relative text-left rounded-2xl border p-5 transition-all active:scale-[0.99] overflow-hidden
        ${selected
          ? 'border-[#00E5FF] bg-[#00E5FF]/10 shadow-[0_0_24px_rgba(0,229,255,0.15)]'
          : plan.popular
            ? 'border-violet-500/40 bg-violet-500/5 hover:border-violet-400/60'
            : 'border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.02] hover:border-slate-300 dark:border-white/25'}`}
    >
      {plan.popular && !selected && (
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-violet-500/20 text-violet-300 border border-violet-500/30 px-2 py-0.5 rounded-full text-[10px] font-bold">
          <Sparkles className="w-3 h-3" />
          Most popular
        </span>
      )}
      {selected && (
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-[#00E5FF] text-[#0A0A0A] px-2 py-0.5 rounded-full text-[10px] font-bold">
          <Check className="w-3 h-3" /> Selected
        </span>
      )}
      <div className="w-9 h-9 rounded-lg bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 flex items-center justify-center mb-3">
        {plan.id === 'vip' ? <Crown className="w-4 h-4 text-amber-300" /> : <Shield className="w-4 h-4 text-[#00E5FF]" />}
      </div>
      <p className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-500 mb-1 font-bold">{plan.tagline}</p>
      <h3 className="font-['Outfit'] text-lg font-bold text-[#0A0A0A] dark:text-white">{plan.name}</h3>
      <p className="font-['Outfit'] text-2xl font-black text-[#0A0A0A] dark:text-white mt-1 mb-3">{money(plan.price_monthly)}{plan.price_suffix || ''}<span className="text-xs text-slate-500 dark:text-slate-500 font-normal ml-1">/mo</span></p>
      <ul className="space-y-1.5">
        {plan.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check className="w-3 h-3 text-[#00E5FF] mt-1 flex-shrink-0" />
            <span className="text-[11px] text-slate-400 dark:text-slate-400 leading-relaxed">{f}</span>
          </li>
        ))}
      </ul>
    </button>
  );
}

function HostingSlider({ tiers, selectedId, onSelect, disabled }) {
  const idx = Math.max(0, tiers.findIndex(t => t.id === selectedId));
  const current = tiers[idx] || tiers[0];
  return (
    <div data-testid="hosting-slider" className={`rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.02] p-5 ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-500 font-bold">Hosting Speed</p>
          <h4 className="font-['Outfit'] text-lg font-bold text-[#0A0A0A] dark:text-white">{current.name} <span className="text-xs text-slate-500 dark:text-slate-500 font-normal">· {current.ram}</span></h4>
        </div>
        <div className="text-right">
          <p className="font-['Outfit'] text-xl font-black text-[#00E5FF]">
            {current.monthly_surcharge === 0 ? 'Included' : `+${money(current.monthly_surcharge)}`}
          </p>
          {current.monthly_surcharge > 0 && <p className="text-[10px] text-slate-500 dark:text-slate-500">/month</p>}
        </div>
      </div>
      <p className="text-xs text-slate-400 dark:text-slate-400 mb-4">{current.description}</p>
      <input
        type="range"
        data-testid="hosting-range"
        min={0}
        max={tiers.length - 1}
        step={1}
        value={idx}
        onChange={(e) => onSelect(tiers[Number(e.target.value)])}
        className="w-full accent-[#00E5FF] cursor-pointer"
      />
      <div className="flex justify-between mt-2">
        {tiers.map((t, i) => (
          <button
            key={t.id}
            type="button"
            data-testid={`hosting-tick-${t.id}`}
            onClick={() => onSelect(t)}
            className={`text-[9px] font-semibold uppercase tracking-wider transition-colors ${i === idx ? 'text-[#00E5FF]' : 'text-slate-400 dark:text-slate-600 hover:text-slate-400 dark:text-slate-300'}`}
          >
            {t.name}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function StepCareHosting({
  carePlans, hostingTiers, warnings,
  carePlan, skipCare, onSelectCare, onSkipCare,
  hosting, onSelectHosting,
  forcedByBoltOns = false, forcedReason = [],
}) {

  // If the user has any care-required power-ups, we prevent them from skipping care.
  const skipDisabled = forcedByBoltOns;
  const effectiveSkip = forcedByBoltOns ? false : skipCare;

  return (
    <div data-testid="step-ecosystem" className="animate-[fadeIn_0.4s_ease]">
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-widest text-[#00E5FF] mb-2 font-bold">Step 4 · The Ecosystem</p>
        <h2 className="font-['Outfit'] text-2xl sm:text-4xl font-bold text-[#0A0A0A] dark:text-white leading-tight">Keep it running smooth</h2>
        <p className="text-sm text-slate-400 dark:text-slate-400 mt-2 max-w-md">Add a Care Plan to bundle hosting, your admin panel and ongoing support — or skip it entirely and walk away with just the build files.</p>
      </div>

      {forcedByBoltOns && (
        <div data-testid="care-forced-banner" className="mb-5 rounded-xl border border-amber-300/60 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/[0.07] p-4">
          <p className="text-xs sm:text-sm font-semibold text-amber-900 dark:text-amber-200 mb-1">A Care Plan is required for your selected power-ups</p>
          <p className="text-[11px] sm:text-xs text-amber-800/90 dark:text-amber-200/80 leading-relaxed">
            {forcedReason.length > 0 ? `Because you've added: ${forcedReason.join(', ')}. ` : ''}
            These run on our hosting & admin infrastructure, so a Care Plan is needed to keep them working after launch.
          </p>
        </div>
      )}

      {/* CARE PLANS */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-['Outfit'] text-base font-bold text-[#0A0A0A] dark:text-white">Care Plan</h3>
          <button
            type="button"
            data-testid="skip-care-toggle"
            disabled={skipDisabled}
            title={skipDisabled ? 'Required by your selected power-ups' : ''}
            onClick={() => !skipDisabled && onSkipCare(!skipCare)}
            className={`text-[11px] px-3 py-1 rounded-full border transition-all ${
              skipDisabled
                ? 'bg-slate-100 dark:bg-white/[0.02] border-slate-200 dark:border-white/5 text-slate-400 dark:text-slate-600 cursor-not-allowed opacity-60'
                : effectiveSkip
                  ? 'bg-slate-200 dark:bg-white/10 border-slate-300 dark:border-white/20 text-[#0A0A0A] dark:text-white'
                  : 'border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-500 hover:text-slate-400 dark:hover:text-slate-300'
            }`}
          >
            {effectiveSkip ? '✓ No care plan' : 'No care plan'}
          </button>
        </div>
        {!effectiveSkip && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {carePlans.map(p => (
              <CarePlanCard key={p.id} plan={p} selected={carePlan?.id === p.id} onSelect={onSelectCare} />
            ))}
          </div>
        )}
      </div>

      {/* HOSTING — only shown when a care plan is selected */}
      {!effectiveSkip ? (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <h3 className="font-['Outfit'] text-base font-bold text-[#0A0A0A] dark:text-white">Hosting</h3>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-widest bg-emerald-100 dark:bg-emerald-500/10 border border-emerald-300/50 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-400">
              Bundled with care
            </span>
          </div>
          <HostingSlider tiers={hostingTiers} selectedId={hosting?.id} onSelect={onSelectHosting} />
        </div>
      ) : (
        /* CODE-EXPORT WARNING — when skipping care, hosting is also skipped */
        <div data-testid="code-export-warning" className="rounded-2xl border-2 border-rose-300 dark:border-rose-500/40 bg-rose-50 dark:bg-rose-500/5 p-5 sm:p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-rose-500/10 border border-rose-500/30 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-rose-500 dark:text-rose-400" />
            </div>
            <div className="flex-1">
              <h4 className="font-['Outfit'] text-base font-bold text-rose-700 dark:text-rose-300 mb-1">Code-export only</h4>
              <p className="text-xs text-rose-700/80 dark:text-rose-200/80 leading-relaxed">
                Without a care plan you'll receive the build files only. You're responsible for hosting, updates, and any future changes.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
            {warnings.map((w, i) => (
              <div key={i} className="flex items-start gap-2 px-3 py-2 rounded-lg bg-white/40 dark:bg-rose-500/5 border border-rose-200/50 dark:border-rose-500/10">
                <span className="w-1 h-1 rounded-full bg-rose-500 dark:bg-rose-400 mt-2 flex-shrink-0" />
                <span className="text-xs text-rose-800 dark:text-rose-200/80 leading-snug">{w}</span>
              </div>
            ))}
          </div>

          <div className="flex items-start gap-2 pt-3 border-t border-rose-300/50 dark:border-rose-500/20">
            <Sparkles className="w-3.5 h-3.5 text-[#00E5FF] mt-0.5 flex-shrink-0" />
            <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed">
              Want hosting + admin panel + ongoing support? <button type="button" onClick={() => onSkipCare(false)} className="text-[#00E5FF] font-semibold hover:underline">Pick a care plan</button> and the hosting slider unlocks.
            </p>
          </div>
        </div>
      )}

      {/* Promote the admin panel only when user is opting out of a care plan */}
      {effectiveSkip && (
        <div className="mt-5">
          <AdminPanelShowcase variant="compact" />
        </div>
      )}
    </div>
  );
}
